import { QuickActionsModalProps } from '@/renderer/types/sales';
import { CalendarDays, FileText, X } from 'lucide-react';
import React from 'react';
import { Button } from '../common';

export const QuickActionsModal: React.FC<QuickActionsModalProps> = ({
  isOpen,
  outletName,
  businessDayLabel,
  isLoadingSalesReport = false,
  isOpeningNewDay = false,
  canOpenNewDay = true,
  onClose,
  onViewSalesReport,
  onOpenNewDay,
}) => {
  if (!isOpen) return null;

  const isBusy = isLoadingSalesReport || isOpeningNewDay;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div
        className="absolute inset-0 bg-black/45 backdrop-blur-[1px]"
        onClick={isBusy ? undefined : onClose}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && !isBusy) onClose();
        }}
        role="button"
        tabIndex={0}
        aria-label="Close quick actions modal"
      />

      <div className="relative w-full max-w-3xl overflow-hidden rounded-md bg-white text-black shadow-2xl">
        <div className="flex items-start justify-between border-b border-gray-200 px-6 py-5">
          <div>
            <h2 className="text-xl font-semibold text-black">Quick Actions</h2>
            <p className="mt-1 text-sm text-black/70">
              {outletName} | Business day {businessDayLabel}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={isBusy}
            className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-black disabled:opacity-50"
            aria-label="Close quick actions modal"
          >
            <X size={22} />
          </button>
        </div>

        <div
          className={`grid gap-4 px-6 py-6 ${canOpenNewDay ? 'sm:grid-cols-2' : 'sm:grid-cols-1'}`}
        >
          <Button
            type="button"
            variant="primary"
            size="lg"
            isLoading={isLoadingSalesReport}
            disabled={isBusy}
            onClick={onViewSalesReport}
            className="flex h-28 flex-col items-center justify-center gap-2 rounded-md bg-primary px-4 text-center text-sm font-semibold uppercase tracking-wide text-white hover:bg-primary/90"
          >
            <FileText size={28} />
            <div className="flex flex-col gap-1 text-left">
              <span>View Sales Report</span>
              <span className="text-md font-medium normal-case tracking-normal text-white/85">
                Review today&apos;s outlet sales and settlement breakdown.
              </span>
            </div>
          </Button>

          {canOpenNewDay ? (
            <Button
              type="button"
              variant="primary"
              size="lg"
              isLoading={isOpeningNewDay}
              disabled={isBusy}
              onClick={onOpenNewDay}
              className="flex h-28 flex-col items-center justify-center gap-4 rounded-md bg-primary px-4 text-center text-sm font-semibold uppercase tracking-wide text-white hover:bg-primary/90"
            >
              <CalendarDays size={28} />
              <div className="flex flex-col gap-1 text-left">
                <span>Open New Day</span>
                <span className="text-md font-medium normal-case tracking-normal text-white/85">
                  Select the next business date and close the current day
                  safely.
                </span>
              </div>
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
};
